import React, { ReactElement } from 'react';
import { Card } from '../../../../components/Card/Card';
import { graphql } from '../../../../generated';
import { withSuspendedCard } from '../../../../util/suspense';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { t } from '../../../../locales/translate';
import { KeyValueTable } from '../../../../components/KeyValueTable/KeyValueTable';
import { NavLink } from 'react-router-dom';
import { voucherCampaignPath } from '../../../../routes';
import { Icon } from '../../../../components/Icon/Icon';
import { formatCurrency, formatPercentage } from '../../../../util/formatters';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { handleError, handleSuccess } from '../../../../model/notification';
import { FormForm } from '../../../../components/Form/FormForm';
import { loadOrderWithItemGroups } from '../../../../model/order';

const ORDER_VOUCHER_DETAILS = graphql(`
  query GetOrderVoucherDetails($id: ID!) {
    order(id: $id) {
      id
      permissions {
        id
        applyVoucherCode
        removeVoucherCode
      }
      voucherCode {
        id
        code
        voucherCampaignId
        voucherCampaign {
          id
          title
          amount
          discountType
        }
      }
    }
  }
`);

const APPLY_VOUCHER_CODE_TO_ORDER = graphql(`
  mutation ApplyVoucherCodeToOrder($orderId: ID!, $code: String!) {
    applyVoucherCode(input: { orderId: $orderId, code: $code }) {
      clientMutationId
      order {
        id
        voucherCode {
          id
        }
      }
    }
  }
`);

const REMOVE_VOUCHER_CODE_FROM_ORDER = graphql(`
  mutation RemoveVoucherCodeFromOrder($orderId: ID!) {
    removeVoucherCode(input: { orderId: $orderId }) {
      clientMutationId
      order {
        id
        voucherCode {
          id
        }
      }
    }
  }
`);

interface Props {
  orderId: string;
}

function VoucherDetails$({ orderId }: Props): ReactElement | null {
  const { data } = useSuspenseQuery(ORDER_VOUCHER_DETAILS, {
    variables: {
      id: orderId,
    },
  });
  const [applyVoucherCode] = useMutation(APPLY_VOUCHER_CODE_TO_ORDER);
  const [removeVoucherCode] = useMutation(REMOVE_VOUCHER_CODE_FROM_ORDER, { onError: (error) => handleError(error) });

  const voucherCode = data?.order?.voucherCode;
  const amount =
    voucherCode?.voucherCampaign?.discountType === 'ABSOLUTE'
      ? formatCurrency(voucherCode?.voucherCampaign?.amount)
      : formatPercentage(voucherCode?.voucherCampaign?.amount);
  const permissions = data.order?.permissions;

  const { fields, model, onSubmit, reset, valid } = useForm<{
    voucherCode: string;
  }>({
    model: {
      voucherCode: '',
    },
    onSubmit: async () => {
      await applyVoucherCode({
        variables: {
          orderId,
          code: model.voucherCode,
        },
      });
      await loadOrderWithItemGroups(orderId);

      handleSuccess(t.model.order.voucherDetails.addedSuccessfully);
    },
    validations: {
      voucherCode: ({ model }) => {
        return model.voucherCode === '' ? ['required'] : [];
      },
    },
    onSubmitError: handleError,
  });

  if (!voucherCode && !permissions?.applyVoucherCode) {
    return null;
  }

  return (
    <Card
      title={t.model.order.voucherDetails.title}
      actions={
        !!voucherCode?.voucherCampaignId && (
          <>
            <NavLink to={voucherCampaignPath(voucherCode?.voucherCampaignId || '')}>
              <Icon name="external-link" />
            </NavLink>
            {permissions?.removeVoucherCode && (
              <Icon
                name="trash"
                onClick={async () => {
                  await removeVoucherCode({ variables: { orderId } });
                  await loadOrderWithItemGroups(orderId);

                  handleSuccess(t.model.order.voucherDetails.removedSuccessfully);
                  reset();
                }}
              />
            )}
          </>
        )
      }
    >
      {!voucherCode ? (
        <FormForm hideCancel canSubmit={valid} onSubmit={onSubmit}>
          <FormTextField {...fields.voucherCode} label={t.model.order.voucherDetails.enterCode} required />
        </FormForm>
      ) : (
        <KeyValueTable
          items={[
            { key: t.model.order.voucherDetails.campaign, value: voucherCode?.voucherCampaign?.title },
            { key: t.model.order.voucherDetails.code, value: voucherCode?.code },
            {
              key: t.model.order.voucherDetails.discount,
              value: amount,
            },
          ]}
          gap="16"
        />
      )}
    </Card>
  );
}

export const VoucherDetails = withSuspendedCard(VoucherDetails$, {
  loadingTitle: t.model.order.voucherDetails.title,
});
